import { Dialog, DialogContent, DialogTitle } from "@mui/material";
import { ColDef, IServerSideDatasource, RowClickedEvent, ValueFormatterParams } from "ag-grid-community";
import { useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useWatch } from "react-hook-form";
import { useMutation } from "@tanstack/react-query";
import { AxiosError } from "axios";
import AgGridCustomTable from "../../../util/component/AgGridCustomTable";
import { getCustomers, IGetCustomersParams } from "../api/services";
import { ICustomer, InvoiceType } from "../types";
import DialogCloseButton from "./DialogCloseButton";
import { IFormValues } from "../pages/insert/types";
import CustomLoader from "../../../util/component/CustomLoader";

interface ISearchCustomerDialogProps {
    section: 'invoice' | 'firstDriver' | 'additionalDriver' | 'bookingOwner',
    open: boolean,
    onClose: () => void,
    onCustomerSelected: (selectedCustomer: ICustomer) => void,
    customersFilter: IGetCustomersParams['filters'] | null
}

const DialogSearchCustomer = (props: ISearchCustomerDialogProps) => {

    const { t } = useTranslation('booking');

    const invoiceType = useWatch<IFormValues, "invoice.type">({
        name: 'invoice.type'
    });

    const customerListMutation = useMutation(getCustomers);

    const columnDefsPerson = useMemo<ColDef<ICustomer>[]>(() => [
                {
                    field: 'firstName',
                    headerName: t('label.firstName'),
                    width: 135
                },
                {
                    field: 'lastName',
                    headerName: t('label.lastName'),
                    width: 135
                },
                {
                    field: 'fiscalCode',
                    headerName: t('label.fiscalCode'),
                    width: 140
                },
                {
                    field: 'drivingLicenseNumber',
                    headerName: t('label.drivingLicenseNumber'),
                    width: 140
                },
                {
                    field: 'birthDate',
                    headerName: t('label.birthDate'),
                    width: 130
                },
                {
                    field: 'birthPlace',
                    headerName: t('label.placeOfBirth'),
                    width: 140
                },
                {
                    field: 'email',
                    headerName: t('label.email'),
                    width: 200
                },
            ], [t]);

    const columnDefsCompany = useMemo<ColDef<ICustomer>[]>(() =>
            [
                {
                    field: 'companyName',
                    headerName: t('label.companyName'),
                    width: 135
                },
                {
                    field: 'vatNumber',
                    headerName: t('label.vatNumber'),
                    width: 135
                },
                {
                    field: 'legalEntity',
                    headerName: t('label.legalForm'),
                    width: 135,
                    valueFormatter: (params: ValueFormatterParams<ICustomer>) => !!params.data?.legalEntity ? t(`legalForms.${params.data?.legalEntity}`) : ''
                },
                {
                    field: 'email',
                    headerName: t('label.email'),
                    width: 130
                },
                {
                    field: 'certifiedEmail',
                    headerName: t('label.certifiedEmailAddress'),
                    width: 140
                },
                {
                    field: 'invoiceCode',
                    headerName: t('label.invoiceCode'),
                    width: 130
                }
            ]
        , [t]);
    const defaultColDef = useMemo<ColDef>(() => ({
        suppressMovable: true,
        suppressMenu: true,
        resizable: true
    }), []);
    const serverSideDatasource = useMemo<IServerSideDatasource>(() => ({
        getRows: async (params) => {
            const customersFilter = props.customersFilter;
            const start = params.request.startRow;
            const limit = (params.request.endRow !== undefined && params.request.startRow !== undefined) ? (params.request.endRow - params.request.startRow) : undefined;

            customerListMutation.mutateAsync({
                filters: customersFilter,
                start,
                limit
            }).then((response: {
                    list: ICustomer[],
                    count: number
                }) => {
                    params.success({
                        rowData: response.list,
                        rowCount: response.count
                    });

                    if (response.count === 0) {
                        params.api.showNoRowsOverlay();
                    }
                }).catch((err: AxiosError) => {
                    params.fail();
                    params.api.hideOverlay();
                });
        }
    }), [props.customersFilter])

    const onRowClicked = useCallback((event: RowClickedEvent) => {
        props.onCustomerSelected(event.data);
        props.onClose();
    }, [props.onCustomerSelected]);

    return (
        <Dialog
            open={props.open}
            onClose={props.onClose}
            fullWidth
            maxWidth="lg"
        >
            <DialogCloseButton onClick={props.onClose} />
            <DialogTitle>
                {t('label.customers')}
            </DialogTitle>
            <DialogContent>
                <AgGridCustomTable
                    height="550px"
                    rowModelType="serverSide"
                    serverSideDatasource={serverSideDatasource}
                    serverSideInfiniteScroll
                    columnDefs={invoiceType !== InvoiceType.PERSON && props.section === 'invoice' ? columnDefsCompany : columnDefsPerson}
                    defaultColDef={defaultColDef}
                    rowStyle={{
                        cursor: 'pointer',
                    }}
                    onRowClicked={onRowClicked}
                />
                <CustomLoader loading={customerListMutation.isLoading} />
            </DialogContent>
        </Dialog>
    );
}

export default DialogSearchCustomer;
